import React, { useState, useEffect } from 'react';
import { TextInput, useNotify, usePermissions, useInput } from 'react-admin';
import { Button, CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@material-ui/core';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Refresh as RefreshIcon } from '@material-ui/icons';

const AccidentDetailsInput = (props) => {
    const [isPolishing, setIsPolishing] = useState(false);
    const [currentValue, setCurrentValue] = useState('');
    const [polishedText, setPolishedText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const notify = useNotify();
    const { permissions } = usePermissions();
    
    // Use the useInput hook to properly integrate with react-admin form
    const { input, meta } = useInput({ source: 'AccidentDetails' });

    // Update currentValue when input changes
    useEffect(() => {
        const value = input.value || '';
        setCurrentValue(value);
    }, [input.value]);

    const handlePolishClick = async () => {
        if (!currentValue || currentValue.trim() === '') {
            notify('Please enter accident details before polishing', 'warning');
            return;
        }

        setIsPolishing(true);
        setOriginalText(currentValue);
        
        try {
            const token = localStorage.getItem('token');
            const apiBaseUrl = process.env.REACT_APP_POLISH_API_URL || process.env.REACT_APP_GO_CONNECT_URL || 'https://www.lamsaz.com';
            const response = await fetch(`${apiBaseUrl}/AccidentReports/polish`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    notes: currentValue
                })
            });

            if (!response.ok) {
                throw new Error('Failed to polish text');
            }

            const data = await response.json();
            setPolishedText(data.polished_notes);
            setIsDialogOpen(true);
        } catch (error) {
            notify('Failed to polish text. Please try again.', 'error');
            console.error('Error polishing text:', error);
        } finally {
            setIsPolishing(false);
        }
    };

    const handleAccept = () => {
        // Update the form field using the input.onChange method
        input.onChange(polishedText);
        setCurrentValue(polishedText);
        setIsDialogOpen(false);
        notify('Accident details updated with polished text', 'success');
    };

    const handleReject = () => {
        setIsDialogOpen(false);
        notify('Polished text rejected', 'info');
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const apiBaseUrl = process.env.REACT_APP_POLISH_API_URL || process.env.REACT_APP_GO_CONNECT_URL || 'https://www.lamsaz.com';
            const response = await fetch(`${apiBaseUrl}/AccidentReports/polish`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    notes: originalText
                })
            });

            if (!response.ok) {
                throw new Error('Failed to polish text');
            }

            const data = await response.json();
            setPolishedText(data.polished_notes);
        } catch (error) {
            notify('Failed to refresh polished text. Please try again.', 'error');
            console.error('Error refreshing polished text:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTextChange = (event) => {
        const newValue = event.target.value;
        setCurrentValue(newValue);
        
        // Also call the input.onChange method
        input.onChange(newValue);
    };

    const isDisabled = isPolishing || !currentValue || currentValue.trim() === '';

    return (
        <Box>
            <Box display="flex" alignItems="center" mb={1}>
                <span><b><u>Accident Details:</u></b></span>
                <Box ml={2}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={isPolishing ? <CircularProgress size={16} /> : <EditIcon />}
                        onClick={handlePolishClick}
                        disabled={isDisabled}
                        style={{ 
                            backgroundColor: '#f0f0f0',
                            borderColor: '#ccc',
                            color: '#333',
                            textTransform: 'none'
                        }}
                    >
                        {isPolishing ? 'Polishing...' : 'Polish'}
                    </Button>
                </Box>
            </Box>
            
            <TextInput 
                {...input}
                {...props}
                fullWidth
                multiline
                rows={4}
                onChange={handleTextChange}
                value={currentValue}
            />
            
            <Dialog 
                open={isDialogOpen} 
                onClose={handleReject}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">AI-Polished Accident Details</Typography>
                </DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Original:
                        </Typography>
                        <Typography variant="body2" style={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            marginBottom: '16px'
                        }}>
                            {originalText}
                        </Typography>
                    </Box>
                    
                    <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Polished:
                        </Typography>
                        <Typography variant="body2" style={{ 
                            backgroundColor: '#e8f5e8', 
                            padding: '8px', 
                            borderRadius: '4px'
                        }}>
                            {polishedText}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleRefresh} 
                        startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        disabled={isLoading}
                        color="default"
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button onClick={handleReject} startIcon={<CloseIcon />} color="secondary">
                        Reject
                    </Button>
                    <Button onClick={handleAccept} startIcon={<CheckIcon />} color="primary" variant="contained">
                        Accept
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AccidentDetailsInput;
